import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X, Sparkles, Loader2, AlertTriangle, CheckCircle2,
  ZoomIn, ZoomOut, RotateCcw,
} from 'lucide-react';

import {
  cancelGenerateAi,
  generateAiCandidateAssetUrl,
  getGenerateAiJob,
  getMatter,
  postGenerateAi,
  selectGenerateAiCandidate,
  type GenerateAiCandidate,
  type GenerateAiJobState,
  type GenerateAiStyle,
} from '@/api/client';
import { useDrawingStore } from '@/store/drawingStore';
import type { Claim } from '@/types';
import { describeApiError } from '@/lib/describeApiError';

interface Props {
  /** Forwarded from DrawingPage. Used by the submit / cancel /
   * polling calls for the matter-scoped routes. */
  matterId: string;
}

const inputClass =
  'flex h-9 w-full rounded-md border border-zinc-300 bg-white px-3 py-1.5 ' +
  'text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]';

const primaryBtn =
  'inline-flex h-9 items-center justify-center gap-2 rounded-sm bg-[#002FA7] px-4 ' +
  'text-sm font-medium text-white hover:bg-[#002277] disabled:opacity-50 transition-colors';

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'transition-colors';

const dangerBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-amber-300 ' +
  'bg-white px-3 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50 ' +
  'transition-colors';

const STYLE_OPTIONS: { value: GenerateAiStyle; label: string }[] = [
  { value: 'schematic',       label: 'Schematic' },
  { value: 'flowchart',       label: 'Flowchart' },
  { value: 'cross_section',   label: 'Cross-section' },
  { value: 'block_diagram',   label: 'Block diagram' },
  { value: 'system_overview', label: 'System overview' },
];

function styleLabel(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  return STYLE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

const POLL_INTERVAL_MS = 2_000;
const TERMINAL_STATUSES = new Set<GenerateAiJobState['status']>([
  'completed', 'failed', 'cancelled',
]);

type View = 'form' | 'progress' | 'completed' | 'failed' | 'cancelled';

/**
 * Drawing module — AI figure generation modal (slice 7G.2).
 *
 * Three-way 7G split (recorded in journal):
 *   - 7G.1 wiring foundations (shipped).
 *   - 7G.2 (this slice): form + progress + cancel views. Drafter can
 *     submit a job, watch progress, and cancel. The completion view
 *     is a stub — slice 7G.3 swaps it for the 3-up candidate gallery
 *     + selection wiring.
 *   - 7G.3 candidate gallery + selection + discard.
 *
 * Polling state lives ENTIRELY inside this component (matterId +
 * jobId + jobState + view) so a modal-close cleanly tears down the
 * setInterval via useEffect cleanup. The store only knows whether
 * the modal is open.
 */
export default function GenerateAiModal({ matterId }: Props) {
  const closeGenerationModal = useDrawingStore((s) => s.closeGenerationModal);
  const refreshLists = useDrawingStore((s) => s.refreshLists);
  const setActiveFigureId = useDrawingStore((s) => s.setActiveFigureId);
  const setMutationError = useDrawingStore((s) => s.setMutationError);
  // BUG-DRAW-008 — surface a "continues in background" hint when
  // the drafter closes the modal mid-progress; the header pill /
  // header-bar polling loop continues to track the job and will
  // land the terminal state on its own.
  const showToast = useDrawingStore((s) => s.showToast);
  // BUG-060 — job state lives in the store. Polling runs at module
  // scope; modal close only tears down the UI, never the loop.
  const storeJobId = useDrawingStore((s) => s.activeGenerationJobId);
  const storeJobState = useDrawingStore((s) => s.generationJobState);
  const storeJobStatus = useDrawingStore((s) => s.generationJobStatus);
  const setGenerationJob = useDrawingStore((s) => s.setGenerationJob);
  const clearGenerationJob = useDrawingStore((s) => s.clearGenerationJob);

  // ── Form state ────────────────────────────────────────────────
  // NB-21 — the form values live in the store draft, and only a
  // value the drafter set by hand is ever written there. The
  // defaults below apply to fields the drafter has not touched;
  // nothing (remount, reopen from the pill, a late async update)
  // can put a default back over an explicit choice.
  const draft = useDrawingStore((s) => s.generationFormDraft);
  const patchDraft = useDrawingStore((s) => s.patchGenerationFormDraft);
  const clearDraft = useDrawingStore((s) => s.clearGenerationFormDraft);
  const intentText = draft.intentText ?? '';
  // Block diagram replaces schematic as the default per drafter-
  // workflow audit — block diagram is the most universally
  // applicable patent-figure style (shows components + their
  // relationships without implying physical topology). Drafters
  // can still pick any of the five style presets.
  const style: GenerateAiStyle = draft.style ?? 'block_diagram';
  const candidateCount = draft.candidateCount ?? 3;
  // BUG-013 Phase 11 — optional target figure scope. null means
  // "next figure in the plan" (legacy behaviour, what the backend
  // picks when figure_id is omitted). When set, the worker
  // matches the drawing_figures.fig_label against drawing_plan's
  // fig_number entries so the drafter can target FIG.2 explicitly
  // when the matter has multiple figures.
  const targetFigureId = draft.targetFigureId ?? null;
  const setIntentText = useCallback(
    (v: string) => patchDraft({ intentText: v }), [patchDraft],
  );
  const setStyle = useCallback(
    (v: GenerateAiStyle) => patchDraft({ style: v }), [patchDraft],
  );
  const setCandidateCount = useCallback(
    (v: number) => patchDraft({ candidateCount: v }), [patchDraft],
  );
  const setTargetFigureId = useCallback(
    (v: string | null) => patchDraft({ targetFigureId: v }), [patchDraft],
  );
  const figures = useDrawingStore((s) => s.figures);

  // Frozen claims count, fetched lazily on mount. Drives the
  // informational coverage line under the intent textarea. The
  // previous per-claim chip selector was retired (per the
  // drafter-workflow audit): drawing_plan's prompt mandates
  // "every element of every independent claim is illustrated in
  // at least one figure" — deselecting claims silently violated
  // that mandate. The backend always uses every frozen claim
  // when grounding_claim_ids is absent / empty.
  const [frozenClaimCount, setFrozenClaimCount] = useState<number | null>(null);
  const [claimsError, setClaimsError] = useState<string | null>(null);

  // ── Job state ─────────────────────────────────────────────────
  // BUG-060 — view is local (it controls the modal step), but
  // jobId/jobState read through the store via mirror state below
  // so reopening the modal mid-job rehydrates the progress view.
  const [view, setView] = useState<View>(() => {
    if (storeJobId && storeJobStatus === 'running') return 'progress';
    if (storeJobId && storeJobStatus === 'queued') return 'progress';
    if (storeJobId && storeJobStatus === 'completed') return 'completed';
    if (storeJobId && storeJobStatus === 'failed') return 'failed';
    if (storeJobId && storeJobStatus === 'cancelled') return 'cancelled';
    return 'form';
  });
  const jobId = storeJobId;
  const jobState = storeJobState;
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Lazy claim count fetch on mount ───────────────────────────
  // Only the count is needed now — the chip selector that
  // consumed the full Claim[] list was retired.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const matter = await getMatter(matterId);
        if (!alive) return;
        const claims: Claim[] = Array.isArray(matter?.claims) ? matter.claims : [];
        const frozen = claims.filter((c) => c.is_frozen);
        setFrozenClaimCount(frozen.length);
      } catch (e: unknown) {
        if (!alive) return;
        const msg = (e as { message?: string })?.message
          || 'Could not load matter claims.';
        setClaimsError(msg);
      }
    })();
    return () => {
      alive = false;
    };
  }, [matterId]);

  // BUG-060 — polling is now module-scoped on the store. The modal
  // only needs to keep `view` in sync with the store's job status
  // so terminal transitions flip the gallery / failed / cancelled
  // panels into view as the poll lands the next status update.
  useEffect(() => {
    if (!storeJobId) return;
    if (storeJobStatus === 'queued' || storeJobStatus === 'running') {
      setView('progress');
    } else if (storeJobStatus === 'completed') {
      setView('completed');
    } else if (storeJobStatus === 'failed') {
      setView('failed');
    } else if (storeJobStatus === 'cancelled') {
      setView('cancelled');
    }
  }, [storeJobId, storeJobStatus]);

  const noClaims = frozenClaimCount !== null && frozenClaimCount === 0;

  const submit = useCallback(async () => {
    setSubmitError(null);
    const trimmed = intentText.trim();
    if (!trimmed) {
      setSubmitError('Describe what you want the figure to show.');
      return;
    }
    setSubmitting(true);
    try {
      // grounding_claim_ids deliberately omitted — the backend
      // treats absent / empty as "use every frozen claim", which
      // is the only behaviour that satisfies the drawing_plan
      // prompt's "every claim element must be illustrated"
      // mandate. The per-claim chip selector that fed this field
      // was retired in the workflow audit.
      const res = await postGenerateAi(matterId, {
        intent_text: trimmed,
        style,
        candidate_count: candidateCount,
        // BUG-013 Phase 11 — null means "next/default figure"
        // (worker picks plan[0]); a set id means "target this
        // figure's fig_label" so multi-figure matters can
        // explicitly pick FIG.N.
        figure_id: targetFigureId,
      });
      // BUG-060 — register the job in the store. The store's
      // setGenerationJob action seeds the initial state and starts
      // the module-scoped polling loop in one call.
      setGenerationJob(matterId, res.job_id, {
        job_id: res.job_id, status: 'queued',
        progress: { step: 'queued', percent: 0 },
        candidates: [],
        error: null,
        created_at: null,
        completed_at: null,
      });
      setView('progress');
    } catch (e: unknown) {
      // describeApiError reads both body shapes the backend uses
      // ({detail: "…"} and {error_code, message}). This used to read only
      // detail.detail, so the 422 for an unconfigured figure-generation
      // provider ({error_code: "provider_invalid", message}) showed as
      // "Request failed with status code 422".
      setSubmitError(
        describeApiError(e).message
        || 'Could not submit the generation request.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [intentText, matterId, style, candidateCount, targetFigureId, setGenerationJob]);

  const updateGenerationProgress = useDrawingStore(
    (s) => s.updateGenerationProgress,
  );

  const cancel = useCallback(async () => {
    if (!jobId || cancelling) return;
    setCancelling(true);
    try {
      const next = await cancelGenerateAi(matterId, jobId);
      updateGenerationProgress(next);
      setView('cancelled');
    } catch (e: unknown) {
      // 409 already-terminal — refresh and let the polling-derived
      // view branch decide what to render.
      try {
        const fresh = await getGenerateAiJob(matterId, jobId);
        updateGenerationProgress(fresh);
        if (TERMINAL_STATUSES.has(fresh.status)) {
          setView(
            fresh.status === 'completed' ? 'completed'
              : fresh.status === 'cancelled' ? 'cancelled'
              : 'failed',
          );
        }
      } catch {
        /* no-op — keep the modal open */
      }
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('generate-ai cancel failed', e);
      }
    } finally {
      setCancelling(false);
    }
  }, [matterId, jobId, cancelling, updateGenerationProgress]);

  const onClose = useCallback(() => {
    // BUG-060 — only tear down the modal UI. The job + polling
    // loop stay alive in the store so the drafter can reopen the
    // modal mid-generation and see the same progress, or pick up
    // the result via the header pill once the job lands terminal.
    closeGenerationModal();
  }, [closeGenerationModal]);

  // ── Gallery selection state (slice 7G.3) ─────────────────────
  const [chosenTempId, setChosenTempId] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);

  const onSelectChosen = useCallback(async () => {
    if (!jobId || !chosenTempId || selecting) return;
    setSelecting(true);
    setSelectError(null);
    try {
      const res = await selectGenerateAiCandidate(matterId, jobId, chosenTempId);
      // Refresh the matter's figure lists so the new figure shows
      // in the sidebar; activate it so the canvas swaps to it.
      await refreshLists(matterId);
      if (res.figure?.id) setActiveFigureId(res.figure.id);
      // Surface the prime-error path the same way slice 5B does for
      // import-from-IDF — non-fatal; figure lands, banner explains.
      if (res.figure?.prime_error) {
        setMutationError(
          `AI figure created, but priming failed: ${res.figure.prime_error}`,
        );
      } else if (res.figure?.prints_figure_label) {
        // NB-08 — the numeral vision pass found a figure label the
        // image model drew; the export adds its own.
        setMutationError(PRINTS_FIGURE_LABEL_NOTE);
      }
      // BUG-060 — drafter committed; clear job + stop polling.
      clearGenerationJob();
      // NB-21 — the figure is made; the next Generate starts from
      // a clean form.
      clearDraft();
      onClose();
    } catch (e: unknown) {
      setSelectError(
        describeApiError(e).message || 'Could not select this candidate.',
      );
    } finally {
      setSelecting(false);
    }
  }, [matterId, jobId, chosenTempId, selecting, refreshLists, setActiveFigureId, setMutationError, onClose, clearGenerationJob, clearDraft]);

  const onDiscardAll = useCallback(() => {
    // No DELETE-job endpoint exists in Phase 7; the slice 7C 24h
    // sweep reclaims the candidate dir. Closing the modal is the
    // user-facing "discard" action — matches the brief's UX intent.
    // BUG-060 — also clear the store-side job so the header pill
    // disappears; otherwise it would keep advertising "Figures
    // ready" after the drafter explicitly discarded.
    clearGenerationJob();
    onClose();
  }, [onClose, clearGenerationJob]);

  // FIX-2C — retry path for the completed-but-empty / failed cases:
  // drop the dead job + its store state, and bounce the modal back
  // to the form so the drafter can re-submit without closing first.
  const onRetry = useCallback(() => {
    clearGenerationJob();
    setSubmitError(null);
    setView('form');
  }, [clearGenerationJob]);

  const progressPercent = jobState?.progress?.percent ?? 0;
  const progressStep = useMemo(() => {
    const step = jobState?.progress?.step ?? 'queued';
    return STEP_LABELS[step] ?? step;
  }, [jobState]);

  // BUG-034 — gate backdrop / Escape dismiss so the user can't
  // lose a running job or unselected candidates by an accidental
  // click. Three states:
  //   * progress  — never dismissable from the backdrop; the X
  //                 button is the explicit close path (see
  //                 onExplicitClose below) and the in-modal
  //                 Cancel button stops the job. Accidental
  //                 backdrop clicks must NOT close mid-progress.
  //   * completed — confirm before dismissing (otherwise candidates
  //                 are abandoned and never promoted to drawing_figures
  //                 so they never appear in the gallery).
  //   * form / failed / cancelled — free to dismiss.
  const onBackdropDismiss = useCallback(() => {
    if (view === 'progress') return;
    if (view === 'completed') {
      if (typeof window === 'undefined') {
        onClose();
        return;
      }
      const ok = window.confirm(
        'Close this dialog? The generated candidates will be discarded — '
        + 'pick one with "Use this figure" first if you want to keep them.',
      );
      if (!ok) return;
    }
    onClose();
  }, [view, onClose]);

  // BUG-DRAW-008 — the X (header close) button is an EXPLICIT
  // dismissal action distinct from the backdrop. The previous
  // behaviour disabled X during the progress view (forcing the
  // drafter to wait 2-3 min or hit Cancel — destroying the job),
  // which left the drafter locked out for the duration of every
  // generation. Per the BUG-060 background-continuation design
  // (onClose just tears down the modal UI; the polling loop and
  // header pill stay alive in the store), closing the modal
  // mid-progress is safe. The drafter can reopen via the header
  // pill or pick up the result when the job lands terminal.
  //
  // X behaviour by view:
  //   * progress  — close immediately; surface a toast
  //                 confirming the job continues in the
  //                 background.
  //   * completed — confirm-then-close (same as backdrop).
  //   * form / failed / cancelled — close.
  const onExplicitClose = useCallback(() => {
    if (view === 'progress') {
      showToast(
        'Generation continues in the background. The header pill will '
        + 'show the result when it lands.',
      );
      onClose();
      return;
    }
    onBackdropDismiss();
  }, [view, onClose, showToast, onBackdropDismiss]);

  // Escape key — same gate. The dialog has aria-modal so focus is
  // trapped here; we only need to listen on the modal root itself.
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    onBackdropDismiss();
  }, [onBackdropDismiss]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-zinc-900/30 px-4 pt-24"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onBackdropDismiss();
      }}
      onKeyDown={onKeyDown}
      tabIndex={-1}
      data-testid="generate-ai-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-ai-title"
    >
      <div
        className={
          // ``max-h-[85vh] flex flex-col`` lets the inner card cap
          // its height to the viewport and split into a sticky
          // header + scrollable body + sticky footer. Without this,
          // the gallery + selected-candidate preview pane stacked
          // taller than the viewport with no scroll affordance —
          // the bug the field reported as "modal opens but I can't
          // select and it's truncated and non-scrollable".
          'flex max-h-[85vh] w-full flex-col '
          + 'rounded-md border border-zinc-200 bg-white shadow-xl '
          // Wider in completed view so 3 cards (~320px each) +
          // gaps fit side-by-side without the cards squishing.
          + (view === 'completed' ? 'max-w-5xl' : 'max-w-lg')
        }
      >
        <header className="flex flex-shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
          <h2
            id="generate-ai-title"
            className="flex items-center gap-2 text-sm font-semibold text-zinc-900"
          >
            <Sparkles className="h-4 w-4 text-[#002FA7]" aria-hidden />
            {VIEW_TITLES[view]}
          </h2>
          <button
            type="button"
            className="text-zinc-400 hover:text-zinc-700"
            onClick={onExplicitClose}
            aria-label="Close"
            data-testid="generate-ai-modal-close"
            title={view === 'progress'
              ? 'Close (generation will continue in the background).'
              : 'Close'}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {view === 'form' && (
          <FormView
            intentText={intentText}
            setIntentText={setIntentText}
            style={style}
            setStyle={setStyle}
            candidateCount={candidateCount}
            setCandidateCount={setCandidateCount}
            targetFigureId={targetFigureId}
            setTargetFigureId={setTargetFigureId}
            figures={figures}
            frozenClaimCount={frozenClaimCount}
            noClaims={noClaims}
            claimsError={claimsError}
            submitting={submitting}
            submitError={submitError}
            onSubmit={submit}
            onCancel={onClose}
          />
        )}

        {view === 'progress' && (
          <ProgressView
            stepLabel={progressStep}
            stepKey={jobState?.progress?.step}
            percent={progressPercent}
            cancelling={cancelling}
            onCancel={cancel}
          />
        )}

        {view === 'completed' && (
          <GalleryView
            matterId={matterId}
            jobId={jobId}
            candidates={jobState?.candidates ?? []}
            consumedTempIds={jobState?.consumed_candidate_temp_ids ?? []}
            chosenTempId={chosenTempId}
            onChoose={setChosenTempId}
            selecting={selecting}
            selectError={selectError}
            onSelectChosen={onSelectChosen}
            onDiscardAll={onDiscardAll}
            onRetry={onRetry}
            stepKey={jobState?.progress?.step}
            jobError={jobState?.error ?? null}
            /* FT-023 + FT-024 (E1 + E2) — pass the matter-wide
               style param + the job's created_at so candidate
               cards can render their differentiating metadata
               and the gallery header can render the expiry
               countdown. */
            /* NB-21 — label the candidates with the style the job
               actually ran with (from the job record), not the
               form's current value. */
            generationStyle={styleLabel(jobState?.style ?? style)}
            jobCreatedAt={jobState?.created_at ?? null}
          />
        )}

        {view === 'failed' && (
          <TerminalView
            kind="failed"
            message={jobState?.error ?? 'Generation failed.'}
            stepKey={jobState?.progress?.step}
            onClose={onClose}
            onRetry={onRetry}
          />
        )}

        {view === 'cancelled' && (
          <TerminalView
            kind="cancelled"
            message="Generation cancelled."
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

const VIEW_TITLES: Record<View, string> = {
  form:      'Generate with AI',
  progress:  'Generating figure',
  completed: 'Generated candidates',
  failed:    'Generation failed',
  cancelled: 'Generation cancelled',
};

// Worker progress.step strings → human labels. Unknown values fall
// back to the raw string so a future worker step shows up rather
// than silently displaying "queued".
const STEP_LABELS: Record<string, string> = {
  queued:                       'Queued',
  starting:                     'Starting…',
  decomposing_claims:           'Decomposing claims…',
  decompose_failed:             'Claim decomposition failed',
  planning_figures:             'Planning figure set…',
  plan_failed:                  'Figure planning failed',
  assigning_numerals:           'Assigning numerals…',
  numeral_assignment_failed:    'Numeral assignment failed',
  generating_figure_spec:       'Generating figure spec…',
  figure_spec_failed:           'Figure-spec generation failed',
  resolving_provider:           'Resolving provider…',
  provider_unresolved:          'Provider unresolved',
  generating_images:            'Generating images…',
  all_candidates_failed:        'All candidates failed',
  tracing_candidates:           'Tracing candidates…',
  all_candidates_unpersisted:   'Could not save candidates',
  completed:                    'Completed',
  cancelled:                    'Cancelled',
  pending_image_generation:     'Awaiting image generation',
  context_build_failed:         'Could not build generation context',
  pending_worker_implementation: 'Worker pending',
};

// ─── Sub-views ───────────────────────────────────────────────────

interface FormViewProps {
  intentText: string;
  setIntentText: (v: string) => void;
  style: GenerateAiStyle;
  setStyle: (v: GenerateAiStyle) => void;
  candidateCount: number;
  setCandidateCount: (v: number) => void;
  targetFigureId: string | null;
  setTargetFigureId: (v: string | null) => void;
  figures: { id: string; fig_label: string | null }[];
  /** Total count of frozen claims for the matter. null while
   *  the count fetch is in flight; 0 when there are no frozen
   *  claims (renders an amber "freeze claims first" hint). */
  frozenClaimCount: number | null;
  noClaims: boolean;
  claimsError: string | null;
  submitting: boolean;
  submitError: string | null;
  onSubmit: () => void;
  onCancel: () => void;
}

function FormView({
  intentText, setIntentText,
  style, setStyle,
  candidateCount, setCandidateCount,
  targetFigureId, setTargetFigureId, figures,
  frozenClaimCount, noClaims, claimsError,
  submitting, submitError,
  onSubmit, onCancel,
}: FormViewProps) {
  const selectedFigureLabel = targetFigureId
    ? (figures.find((f) => f.id === targetFigureId)?.fig_label
       || `Figure ${targetFigureId.slice(0, 6)}`)
    : null;
  const generateDisabled =
    submitting || noClaims || !intentText.trim();
  return (
    <>
      <div className="flex-1 min-h-0 space-y-4 overflow-y-auto px-4 py-4">
        <div>
          <label htmlFor="generate-ai-intent" className="mb-1 block text-xs font-medium text-zinc-700">
            What do you want to show?
          </label>
          <textarea
            id="generate-ai-intent"
            className={`${inputClass} h-24 py-2 resize-none`}
            placeholder={
              'e.g. Sensor topology of the cargo tank, showing temperature '
              + 'sensors 12a–12d, hull heat-flux sensor 28, and the '
              + 'cryogenic tank 10 housing re-liquefaction skid 14.'
            }
            value={intentText}
            onChange={(e) => setIntentText(e.target.value)}
            data-testid="generate-ai-intent"
          />
        </div>

        {/* Informational coverage line — replaces the retired
            per-claim chip selector. The drawing_plan prompt
            requires every claim element to be illustrated, so
            subset selection was silently breaking that contract;
            the modal now confirms the full frozen-claim set will
            be used as the coverage checklist. */}
        <div data-testid="generate-ai-claims-coverage">
          {claimsError && (
            <p className="rounded-sm bg-amber-50 px-2 py-1.5 text-[12px] text-amber-700">
              {claimsError}
            </p>
          )}
          {frozenClaimCount === null && !claimsError && (
            <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading frozen claim count…
            </div>
          )}
          {noClaims && (
            <p className="rounded-sm bg-amber-50 px-2 py-1.5 text-[12px] text-amber-700">
              No frozen claims found. Freeze the claim set in Stage 3 first.
            </p>
          )}
          {frozenClaimCount !== null && frozenClaimCount > 0 && (
            <p className="text-xs text-zinc-500">
              Generating with {frozenClaimCount} frozen
              claim{frozenClaimCount === 1 ? '' : 's'} as the
              coverage checklist.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="generate-ai-style" className="mb-1 block text-xs font-medium text-zinc-700">
              Style preset
            </label>
            <select
              id="generate-ai-style"
              className={inputClass}
              value={style}
              onChange={(e) => setStyle(e.target.value as GenerateAiStyle)}
              data-testid="generate-ai-style"
            >
              {STYLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-700">
              Candidates
            </label>
            <div className="flex gap-1.5" role="radiogroup" aria-label="Number of candidates">
              {[1, 2, 3].map((n) => {
                const selected = candidateCount === n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setCandidateCount(n)}
                    className={
                      'inline-flex h-9 flex-1 items-center justify-center rounded-md text-sm font-medium transition-colors '
                      + (selected
                        ? 'border border-[#002FA7] bg-[#002FA7] text-white'
                        : 'border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50')
                    }
                    data-testid={`generate-ai-count-${n}`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* BUG-013 Phase 11 — target figure scope. Defaults to
            "Next/default" (legacy plan[0] behaviour); set
            explicitly when the matter has multiple figures and
            the drafter wants to generate FIG.N specifically. */}
        {figures.length > 0 && (
          <div>
            <label
              htmlFor="generate-ai-target-figure"
              className="mb-1 block text-xs font-medium text-zinc-700"
            >
              Target figure
            </label>
            <select
              id="generate-ai-target-figure"
              className="w-full rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-800 focus:border-[#002FA7] focus:outline-none"
              value={targetFigureId ?? ''}
              onChange={(e) => setTargetFigureId(e.target.value || null)}
              disabled={submitting}
              data-testid="generate-ai-target-figure"
            >
              <option value="">
                Next / default (plan picks first figure)
              </option>
              {figures.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.fig_label || `Figure ${f.id.slice(0, 6)}`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-zinc-500">
              Pick a specific figure when the matter already has
              multiple figures and you want this generation to
              target a particular slot.
            </p>
            {/* Pipeline-stage transparency note — drawing_plan
                runs matter-wide regardless of the target-figure
                selection; only the downstream figure-render
                stage scopes to the chosen figure. Without this
                note, drafters reasonably assume "target figure
                = single-figure run" and are surprised when the
                plan still considers every claim element. */}
            {selectedFigureLabel && (
              <p
                className="mt-1 text-[11px] text-amber-700"
                data-testid="generate-ai-target-figure-note"
              >
                The drawing plan will still be generated for the
                full matter. Only {selectedFigureLabel} will be
                rendered as a new candidate.
              </p>
            )}
          </div>
        )}

        {submitError && (
          <p className="flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1.5 text-[12px] text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {submitError}
          </p>
        )}
      </div>
      <footer className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3">
        <button
          type="button"
          className={outlineBtn}
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className={primaryBtn}
          onClick={onSubmit}
          disabled={generateDisabled}
          data-testid="generate-ai-submit"
        >
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {submitting ? 'Submitting…' : 'Generate →'}
        </button>
      </footer>
    </>
  );
}

interface ProgressViewProps {
  stepLabel: string;
  stepKey?: string;
  percent: number;
  cancelling: boolean;
  onCancel: () => void;
}

function ProgressView({ stepLabel, percent, cancelling, onCancel }: ProgressViewProps) {
  return (
    <>
      <div className="space-y-3 px-4 py-6" data-testid="generate-ai-progress">
        <div className="flex items-center justify-between text-xs text-zinc-700">
          <span className="font-medium">{stepLabel}</span>
          <span className="tabular-nums text-zinc-500">{percent}%</span>
        </div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-zinc-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div
            className="h-full bg-[#002FA7] transition-[width]"
            style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
          />
        </div>
        {/* NB-05 — the raw worker step key (FIX-2A) is no longer
            shown; it is an internal id, and the label above already
            names the step in plain words. */}
        <p className="text-[11px] text-zinc-500">
          Generation typically takes 30–90 seconds for three candidates.
          You can leave this open or close the modal — the job keeps
          running.
        </p>
      </div>
      <footer className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3">
        <button
          type="button"
          className={dangerBtn}
          onClick={onCancel}
          disabled={cancelling}
          data-testid="generate-ai-cancel"
        >
          {cancelling && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </button>
      </footer>
    </>
  );
}

interface GalleryViewProps {
  matterId: string;
  jobId: string | null;
  candidates: GenerateAiCandidate[];
  /** Cue A2 multi-pick — temp_ids the drafter already promoted.
   *  Cards for these candidates render with a "Used" badge and a
   *  disabled pick action. Empty array on a fresh job. */
  consumedTempIds: string[];
  chosenTempId: string | null;
  onChoose: (tempId: string) => void;
  selecting: boolean;
  selectError: string | null;
  onSelectChosen: () => void;
  onDiscardAll: () => void;
  onRetry: () => void;
  stepKey?: string;
  jobError?: string | null;
  /** FT-023 (E1) — generation-level style param. Surfaced per
   *  card so candidates are visually distinguishable beyond seed
   *  number when the user chose a specific style for the run. */
  generationStyle?: string;
  /** FT-024 (E2) — job createdAt ISO string. Drives the
   *  expiry-countdown chip in the gallery header
   *  (candidates expire ~24h after the job's created_at). */
  jobCreatedAt?: string | null;
}

function GalleryView({
  matterId,
  jobId,
  candidates,
  consumedTempIds,
  chosenTempId,
  onChoose,
  selecting,
  selectError,
  onSelectChosen,
  onDiscardAll,
  onRetry,
  stepKey,
  jobError,
  generationStyle,
  jobCreatedAt,
}: GalleryViewProps) {
  // Resolve the chosen candidate up-front so the useMemo runs on
  // every render of GalleryView (rules-of-hooks). Falls back to the
  // first candidate when nothing's chosen yet so the preview pane
  // has content from first paint; null when the candidates list is
  // empty (the empty-fallback branch below short-circuits before
  // we'd ever read it).
  const chosenCandidate = useMemo(
    () =>
      candidates.find((c) => c.temp_id === chosenTempId)
      ?? candidates[0]
      ?? null,
    [candidates, chosenTempId],
  );

  // FIX-2C — completed-but-empty fallback. The worker normally marks
  // an all-fail job as 'failed', but historically a few code paths
  // (e.g. provider returns zero usable images on every candidate)
  // could land us in 'completed' with an empty list. Show step +
  // any captured error and offer a one-click retry that resets the
  // modal back to the form rather than dead-ending on Close.
  if (candidates.length === 0 || jobId === null) {
    return (
      <>
        <div
          className="flex-1 min-h-0 space-y-3 overflow-y-auto px-4 py-6 text-center"
          data-testid="generate-ai-completed-empty"
        >
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" aria-hidden />
          <p className="text-sm font-medium text-zinc-900">
            Generation completed but no candidates were produced
          </p>
          <div className="mx-auto max-w-md rounded-md border border-amber-200 bg-amber-50 p-3 text-left text-xs text-amber-900">
            <p className="font-mono font-semibold mb-1">
              Step reached: {stepKey ? (STEP_LABELS[stepKey] ?? stepKey) : 'unknown'}
            </p>
            <p className="font-mono break-words">
              {jobError
                || 'The image provider may have rejected the request or returned an empty result.'}
            </p>
            {/* Same conditional as TerminalView: drop the
                Settings pointer when the failure looks
                transient (tier1_parse_failed / "Click Retry"
                / etc.) and route the drafter to the retry
                affordance instead. */}
            {_isTransientFailure(jobError || '') ? (
              <p className="mt-2 text-[11px] text-zinc-600">
                This is usually transient — click{' '}
                <span className="font-medium">Try again</span> to
                regenerate.
              </p>
            ) : (
              <p className="mt-2 text-[11px] text-zinc-600">
                Ask an administrator to check the figure-generation
                provider and its API key in Settings.
              </p>
            )}
          </div>
        </div>
        <footer className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-zinc-200 px-4 py-3">
          <button type="button" className={outlineBtn} onClick={onDiscardAll}>
            Close
          </button>
          <button
            type="button"
            className={primaryBtn}
            onClick={onRetry}
            data-testid="generate-ai-retry"
          >
            Try again
          </button>
        </footer>
      </>
    );
  }

  return (
    <>
      {/* Scrollable body. ``flex-1 min-h-0 overflow-y-auto``
          pairs with the parent's ``max-h-[85vh] flex flex-col``
          to clamp the body's height to "viewport minus header
          minus footer". Without ``min-h-0`` the flex child
          refuses to shrink below its content size and the scroll
          never engages. */}
      <div
        className="flex-1 min-h-0 space-y-3 overflow-y-auto px-4 py-4"
        data-testid="generate-ai-completed"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-zinc-500">
            Generated {candidates.length} candidate{candidates.length === 1 ? '' : 's'}.
            Pick one to add it to the workbench. Unselected candidates
            stay accessible from the Recent Generations panel until
            the 24h cleanup sweep — you can come back and pick another.
          </p>
          {/* FT-024 (E2) — expiry countdown chip. Computes hours
              remaining against jobCreatedAt + 24h; chip turns amber
              when <6h, red when expired. Hidden if jobCreatedAt is
              missing (older deploys, or job started before the
              field shipped). */}
          {jobCreatedAt && (
            <ExpiryCountdown createdAt={jobCreatedAt} />
          )}
        </div>
        <div
          className={
            'grid gap-3 '
            + (candidates.length === 1
              ? 'grid-cols-1'
              : candidates.length === 2
                ? 'grid-cols-2'
                : 'grid-cols-3')
          }
        >
          {candidates.map((cand) => {
            const isConsumed = consumedTempIds.includes(cand.temp_id);
            return (
              <CandidateCard
                key={cand.temp_id}
                matterId={matterId}
                jobId={jobId}
                candidate={cand}
                selected={chosenTempId === cand.temp_id}
                consumed={isConsumed}
                generationStyle={generationStyle}
                onChoose={() => {
                  if (!isConsumed) onChoose(cand.temp_id);
                }}
              />
            );
          })}
        </div>
        {chosenCandidate && (
          <CandidatePreviewPane
            matterId={matterId}
            jobId={jobId}
            candidate={chosenCandidate}
          />
        )}
        {selectError && (
          <p className="flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1.5 text-[12px] text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {selectError}
          </p>
        )}
      </div>
      <footer className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-zinc-200 px-4 py-3">
        <button
          type="button"
          className={outlineBtn}
          onClick={onDiscardAll}
          disabled={selecting}
          data-testid="generate-ai-discard-all"
        >
          Discard all
        </button>
        <button
          type="button"
          className={primaryBtn}
          onClick={onSelectChosen}
          disabled={
            selecting
            || chosenTempId === null
            || (chosenTempId !== null && consumedTempIds.includes(chosenTempId))
          }
          data-testid="generate-ai-select"
        >
          {selecting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {selecting ? 'Adding…' : 'Use this candidate →'}
        </button>
      </footer>
    </>
  );
}

interface CandidateCardProps {
  matterId: string;
  jobId: string;
  candidate: GenerateAiCandidate;
  selected: boolean;
  /** Cue A2 — true when this candidate has already been
   *  promoted into a drawing_figures row. Renders a "Used"
   *  badge and disables the pick affordance. */
  consumed?: boolean;
  /** FT-023 (E1) — generation-run style param, surfaced on
   *  each card so candidates carry a distinguishing chip beyond
   *  the seed number. */
  generationStyle?: string;
  onChoose: () => void;
}

/** NB-08 — shown when a candidate prints its own figure label. */
export const PRINTS_FIGURE_LABEL_NOTE =
  'This candidate prints its own figure label; remove it with the lasso, or pick another.';

function CandidateCard({
  matterId, jobId, candidate, selected, consumed = false,
  generationStyle, onChoose,
}: CandidateCardProps) {
  // Prefer the traced SVG when present (better at small sizes); fall
  // back to the raster otherwise. Both live behind the Phase 7G.3
  // candidate-file endpoints; <img> handles SVG just fine.
  const previewUrl = candidate.is_vectorized && candidate.svg_path
    ? generateAiCandidateAssetUrl(matterId, jobId, candidate.temp_id, 'svg')
    : generateAiCandidateAssetUrl(matterId, jobId, candidate.temp_id, 'raster');
  const durationSec = (candidate.duration_ms / 1000).toFixed(1);
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={selected}
      aria-disabled={consumed}
      title={consumed ? 'Already used in a figure' : undefined}
      className={
        'group flex flex-col rounded-md border bg-white p-2 text-left transition-colors '
        + (consumed
          ? 'border-zinc-200 opacity-60 cursor-not-allowed'
          : selected
            ? 'border-[#002FA7] ring-2 ring-[#002FA7]/30'
            : 'border-zinc-200 hover:border-zinc-300')
      }
      data-testid={`generate-ai-candidate-${candidate.seed}`}
      data-consumed={consumed ? 'true' : 'false'}
    >
      <div className="relative aspect-square overflow-hidden rounded-sm bg-zinc-50">
        <img
          src={previewUrl}
          alt={`Candidate ${candidate.seed}`}
          className="h-full w-full object-contain"
          loading="lazy"
        />
        {consumed && (
          <span className="absolute left-1 top-1 rounded-sm bg-zinc-900/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
            Used
          </span>
        )}
        {!candidate.is_vectorized && (
          <span className="absolute right-1 top-1 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            raster
          </span>
        )}
      </div>
      <p className="mt-2 line-clamp-2 text-[12px] text-zinc-700">
        {candidate.plan_summary || 'Generated candidate'}
      </p>
      {candidate.prints_figure_label && (
        <p
          className="mt-1 text-[11px] text-amber-700"
          data-testid={`generate-ai-candidate-fig-label-${candidate.seed}`}
        >
          {PRINTS_FIGURE_LABEL_NOTE}
        </p>
      )}
      <p className="mt-1 truncate text-[11px] text-zinc-500">
        {candidate.provider}/{candidate.model}
      </p>
      {/* FT-023 (E1) — second metadata line carrying the
          generation style (when set) alongside seed + duration.
          Style is the most useful run-level differentiator
          when all three candidates share the same plan_summary.
          (NB-05: the internal request id is no longer shown.) */}
      <p className="text-[11px] tabular-nums text-zinc-500">
        seed {candidate.seed} · {durationSec}s
        {generationStyle && (
          <>
            {' · '}
            <span className="font-medium text-zinc-600">
              {generationStyle}
            </span>
          </>
        )}
      </p>
    </button>
  );
}

/**
 * FT-024 (E2) — expiry countdown for the generation gallery.
 * Candidates live ~24h after the job's `created_at`; the worker's
 * cleanup sweep deletes the candidate dir at that boundary. We
 * surface remaining hours so the drafter knows when "come back
 * later and pick another" stops working. Re-renders once per
 * minute so the chip stays current without a full gallery
 * refresh.
 */
function ExpiryCountdown({ createdAt }: { createdAt: string }) {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const createdMs = useMemo(() => {
    const t = Date.parse(createdAt);
    return Number.isFinite(t) ? t : null;
  }, [createdAt]);
  if (createdMs === null) return null;
  const expiresAt = createdMs + 24 * 60 * 60 * 1000;
  const msLeft = expiresAt - now;
  const hoursLeft = Math.max(0, Math.floor(msLeft / 3600_000));
  const minutesLeft = Math.max(0, Math.floor((msLeft % 3600_000) / 60_000));
  const expired = msLeft <= 0;
  const tone = expired
    ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
    : hoursLeft < 6
      ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
      : 'bg-zinc-100 text-zinc-600';
  const text = expired
    ? 'Expired'
    : hoursLeft > 0
      ? `Expires in ${hoursLeft}h`
      : `Expires in ${minutesLeft}m`;
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${tone}`}
      data-testid="generate-ai-expiry-countdown"
      title={expired
        ? 'These candidates have been cleaned up server-side; pick another action.'
        : `Candidates are cleaned up 24h after the job started (${new Date(expiresAt).toLocaleString()}).`}
    >
      {text}
    </span>
  );
}

/**
 * CandidatePreviewPane — zoomable inspection view for the currently-
 * chosen candidate. Sits below the 3-up thumbnail grid in
 * GalleryView. The thumbnail aspect-square keeps a complex figure
 * ~250 px wide which is too small to read text labels and confirm
 * fine geometry; this pane gives the drafter scroll + zoom on the
 * full-resolution image without leaving the modal.
 *
 * Zoom contract:
 *   - Click the image → cycles 100 → 150 → 200 → back to 100.
 *   - Plus / minus / Reset buttons step in 50% increments,
 *     clamped to 50–300%.
 *   - Switching the chosen candidate (a different thumbnail) resets
 *     zoom to 100% so the new image shows from a known baseline.
 *
 * The pane reuses the same generateAiCandidateAssetUrl resolution
 * the thumbnail uses (vector when available, raster otherwise).
 */
function CandidatePreviewPane({
  matterId, jobId, candidate,
}: {
  matterId: string;
  jobId: string;
  candidate: GenerateAiCandidate;
}) {
  const [zoom, setZoom] = useState(1);
  // Reset to 100 % whenever the drafter picks a different candidate.
  // Keyed on temp_id so a re-render of the same candidate (e.g. job
  // poll updating sibling fields) doesn't blow away the drafter's
  // current zoom level.
  useEffect(() => {
    setZoom(1);
  }, [candidate.temp_id]);

  const previewUrl = candidate.is_vectorized && candidate.svg_path
    ? generateAiCandidateAssetUrl(matterId, jobId, candidate.temp_id, 'svg')
    : generateAiCandidateAssetUrl(matterId, jobId, candidate.temp_id, 'raster');

  const onImageClick = () => {
    // 100 → 150 → 200 → 100 cycle. Matches the brief's pattern;
    // gives a quick "pop in / pop further / pop out" without
    // hunting for the +/- buttons.
    setZoom((z) => (z >= 2 ? 1 : z + 0.5));
  };

  const zoomPctLabel = `${Math.round(zoom * 100)}%`;

  return (
    <div
      className="rounded-md border border-zinc-200 bg-white"
      data-testid="generate-ai-preview-pane"
    >
      <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-1.5">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Preview · seed {candidate.seed}
        </span>
        <div className="flex items-center gap-1.5 text-xs text-zinc-600">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.5))}
            disabled={zoom <= 0.5}
            data-testid="generate-ai-preview-zoom-out"
            aria-label="Zoom out"
            className="inline-flex h-6 w-6 items-center justify-center rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40"
          >
            <ZoomOut size={11} />
          </button>
          <span
            data-testid="generate-ai-preview-zoom-pct"
            className="min-w-[3rem] text-center font-mono tabular-nums"
          >
            {zoomPctLabel}
          </span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(3, z + 0.5))}
            disabled={zoom >= 3}
            data-testid="generate-ai-preview-zoom-in"
            aria-label="Zoom in"
            className="inline-flex h-6 w-6 items-center justify-center rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40"
          >
            <ZoomIn size={11} />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            disabled={zoom === 1}
            data-testid="generate-ai-preview-zoom-reset"
            aria-label="Reset zoom"
            className="ml-1 inline-flex h-6 items-center gap-1 rounded border border-zinc-200 bg-white px-1.5 hover:bg-zinc-50 disabled:opacity-40"
          >
            <RotateCcw size={10} />
            Reset
          </button>
        </div>
      </div>
      <div
        className="relative max-h-[60vh] overflow-auto bg-zinc-50"
        data-testid="generate-ai-preview-scroll"
      >
        <img
          src={previewUrl}
          alt={`Candidate ${candidate.seed} preview`}
          // ``transformOrigin: top left`` keeps the image anchored
          // so zooming in scrolls naturally rather than jumping the
          // visible region. width:100% means the IMAGE fills the
          // pane at zoom=1; the ``style.width`` scaling at higher
          // zooms produces a horizontally-scrollable larger image
          // — same effect as a CSS ``scale(...)`` transform but
          // works correctly with overflow-auto (transforms don't
          // affect the parent's scrollable area).
          style={{
            width: `${zoom * 100}%`,
            cursor: zoom >= 2 ? 'zoom-out' : 'zoom-in',
          }}
          onClick={onImageClick}
          className="block max-w-none select-none"
          draggable={false}
          data-testid="generate-ai-preview-image"
        />
      </div>
    </div>
  );
}


interface TerminalViewProps {
  kind: 'failed' | 'cancelled';
  message: string;
  stepKey?: string;
  onClose: () => void;
  /** Optional retry handler. When present + the failure looks
   *  transient (parse / analysis / "Retry" hinted), the failed
   *  view surfaces a Retry button alongside Close. */
  onRetry?: () => void;
}

/** Detect failure messages where the "configure provider" hint
 *  would be wrong. Tier-1 parse failures and other transient
 *  AI-side issues are NOT caused by a missing API key; surfacing
 *  the Settings pointer in that case just sends drafters down a
 *  diagnostic dead-end. The backend has converged on
 *  ``tier1_parse_failed`` / "Analysis failed — click Retry."
 *  as the status-code+message convention, so detecting either
 *  signal lets us short-circuit the config copy. */
function _isTransientFailure(message: string): boolean {
  const m = (message || '').toLowerCase();
  return (
    m.includes('tier1_parse_failed')
    || m.includes('tier1_failed')
    || m.includes('parse_failure')
    || m.includes('parse failed')
    || m.includes('click retry')
    || m.includes('analysis failed')
  );
}

function TerminalView({
  kind, message, stepKey, onClose, onRetry,
}: TerminalViewProps) {
  const Icon = AlertTriangle;
  const tone = kind === 'failed' ? 'text-rose-500' : 'text-amber-500';
  const isTransient = kind === 'failed' && _isTransientFailure(message);
  return (
    <>
      <div className="space-y-3 px-4 py-6 text-center" data-testid={`generate-ai-${kind}`}>
        <Icon className={`mx-auto h-10 w-10 ${tone}`} aria-hidden />
        <p className="text-sm font-medium text-zinc-900">{message}</p>
        {/* Step + error detail box for the failed case. The
             Settings pointer is conditional: only shown when the
             message does NOT look like a transient AI-side
             failure. tier1_parse_failed / analysis-failed
             responses get a "Click Retry" line instead, which
             matches the recovery action the underlying issue
             needs. */}
        {kind === 'failed' && (
          <div
            className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-left text-xs text-rose-800 break-words"
            data-testid="generate-ai-failed-detail"
          >
            <p className="font-mono font-semibold mb-1">
              Failed at step: {stepKey ? (STEP_LABELS[stepKey] ?? stepKey) : 'unknown'}
            </p>
            <p className="font-mono">
              {message || 'No error detail available.'}
            </p>
            {isTransient ? (
              <p className="mt-2 text-[11px] text-zinc-600">
                This is usually transient — click{' '}
                <span className="font-medium">Retry</span> to
                regenerate.
              </p>
            ) : (
              <p className="mt-2 text-[11px] text-zinc-600">
                Ask an administrator to check the figure-generation
                provider and its API key in Settings.
              </p>
            )}
          </div>
        )}
      </div>
      <footer className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3">
        <button type="button" className={outlineBtn} onClick={onClose}>
          Close
        </button>
        {onRetry && (
          <button
            type="button"
            className={primaryBtn}
            onClick={onRetry}
            data-testid="generate-ai-terminal-retry"
          >
            Retry
          </button>
        )}
      </footer>
    </>
  );
}
